/**
 * Correcting a shipped order's box sizes (Box Sizes page). The size is chosen exactly as at
 * packing: the same `PackageSizeField`, drafts and rules (`pages/SalesOrderShipping/packageSize`) —
 * one of the vendor's saved sizes, largest first, or a custom size whose fractions are rounded UP
 * to whole inches, as the carriers measure.
 *
 * Server side: the rules live in libs/db/src/utils/ship-packages.ts and the correction in
 * libs/services/src/order/ship-package-edits.ts; the server re-checks everything.
 */
import { gql } from '@apollo/client';
import {
  carrierDimensions,
  dimensionKey,
  packageDimensions,
  savedSizesFor,
  type PackageType,
  type ShipPackageDraft
} from '@/pages/SalesOrderShipping/packageSize';

export type { PackageType, ShipPackageDraft };

/** Mirrors DIM_WEIGHT_DIVISOR (libs/db). */
export const DIM_WEIGHT_DIVISOR = 139;

export const packageTypeLabel = (packageType?: string | null) =>
  packageType === 'bag' ? 'Poly Bag' : 'Box';

export const sizeKey = (dimensions: number[]) => dimensionKey(dimensions);

/** "16 × 12 × 8 in". */
export const formatSize = (dimensions?: (number | string)[] | null) => {
  const sides = dimensions || [];
  return sides.length && sides.every((n) => Number.isFinite(Number(n)))
    ? `${sides.join(' × ')} in`
    : 'No size';
};

/** Carrier dimensional weight of a box, whole pounds; null for a bag or an incomplete size. */
export const dimensionalWeight = (packageType: PackageType, dimensions: number[]) => {
  if (packageType !== 'box' || dimensions.length !== 3) return null;
  if (!dimensions.every((n) => Number.isFinite(n) && n > 0)) return null;
  return Math.ceil((dimensions[0] * dimensions[1] * dimensions[2]) / DIM_WEIGHT_DIVISOR);
};

/** The greater of the actual weight (rounded up) and the dimensional weight, whole pounds. */
export const billableWeight = (
  packageType: PackageType,
  dimensions: number[],
  weight?: number | null
) => {
  const actual = Number.isFinite(Number(weight)) && Number(weight) > 0 ? Math.ceil(Number(weight)) : null;
  const dim = dimensionalWeight(packageType, dimensions);
  if (actual === null) return dim;
  if (dim === null) return actual;
  return Math.max(actual, dim);
};

/** "38 lb", or an em dash when unknown. */
export const formatPounds = (pounds?: number | null) =>
  pounds === null || pounds === undefined || !Number.isFinite(Number(pounds))
    ? '—'
    : `${Number(pounds)} lb`;

/** One recorded package as the Box Sizes page reads it (Order.shipPackages). */
export interface ShipPackage {
  index: number;
  number: number;
  packageType: PackageType;
  dimensions: number[];
  customSize: boolean;
  weight?: number | null;
  dimensionalWeight?: number | null;
  billableWeight?: number | null;
}

/**
 * The packing draft a recorded package opens as: its saved size when the vendor still lists it
 * (and it was not entered by hand), else a custom size prefilled with its sides.
 */
export const draftOf = (pkg: ShipPackage, shippingDimensionOptions: unknown): ShipPackageDraft => {
  const type: PackageType = pkg.packageType === 'bag' ? 'bag' : 'box';
  const key = dimensionKey(pkg.dimensions);
  const saved = savedSizesFor(shippingDimensionOptions, type).map(dimensionKey);
  const [length, width, height] = pkg.dimensions.map(String);
  // Prefilled either way, so switching to Custom size starts from the box it shipped in.
  const custom = { length, width, ...(type === 'box' ? { height } : {}) };
  if (!pkg.customSize && saved.includes(key)) return { type, size: key, sizeMode: 'saved', custom };
  return { type, size: saved[0], sizeMode: 'custom', custom };
};

/** The whole-inch size a draft records (fractions rounded up, as at packing), or null if unusable. */
export const draftDimensions = (draft: ShipPackageDraft): number[] | null => {
  const dimensions = packageDimensions(draft);
  return dimensions ? carrierDimensions(dimensions) : null;
};

/** Whether a draft records something other than the package's size (an unusable draft does). */
export const draftChanges = (pkg: ShipPackage, draft: ShipPackageDraft) => {
  const dimensions = draftDimensions(draft);
  return !dimensions || draft.type !== pkg.packageType || sizeKey(dimensions) !== sizeKey(pkg.dimensions);
};

/** The packages whose usable draft differs from what is recorded, as the mutation takes them. */
export const changedPackages = (
  packages: ShipPackage[],
  drafts: Record<number, ShipPackageDraft | undefined>
) =>
  packages.flatMap((pkg) => {
    const draft = drafts[pkg.index];
    const dimensions = draft && draftDimensions(draft);
    if (!draft || !dimensions || !draftChanges(pkg, draft)) return [];
    return [
      {
        index: pkg.index,
        packageType: draft.type,
        dimensions,
        customSize: draft.sizeMode === 'custom'
      }
    ];
  });

/** Σ billable weight, or null when any package's is unknown. */
export const totalBillableWeight = (
  rows: { packageType: PackageType; dimensions: number[]; weight?: number | null }[]
) => {
  const weights = rows.map((row) => billableWeight(row.packageType, row.dimensions, row.weight));
  return weights.some((w) => w === null)
    ? null
    : weights.reduce((sum: number, w) => sum + (w as number), 0);
};

const SHIP_PACKAGE_FIELDS = `
  shipPackages {
    index
    number
    packageType
    dimensions
    customSize
    weight
    dimensionalWeight
    billableWeight
  }
  shipPackagesRevision
  shipPackagesEditable
  shipPackagesEditBlockedReason
  shipPackageChanges {
    id
    at
    userName
    note
    sourceOrderId
    sourceOrderNumber
    bundleNumber
    changes {
      index
      number
      weight
      from {
        packageType
        dimensions
        customSize
        billableWeight
      }
      to {
        packageType
        dimensions
        customSize
        billableWeight
      }
    }
  }
`;

export const SALES_ORDER_PACKAGES = gql`
  query WarehouseSalesOrderPackages($id: ID!, $vendorId: ID!) {
    salesOrder(id: $id, vendorId: $vendorId) {
      id
      vendorSalesOrderNumber
      vendorStatus
      metadata {
        shippingMethodName
        shippedDate
      }
      trackingCodes {
        number
        carrier
      }
      shippingBundle {
        id
        bundleNumber
        status
        orders {
          id
          vendorSalesOrderNumber
        }
      }
      ${SHIP_PACKAGE_FIELDS}
    }
    company(id: $vendorId) {
      id
      integrationSettings {
        shippingDimensionOptions
      }
    }
  }
`;

export const UPDATE_SHIP_PACKAGES = gql`
  mutation WarehouseUpdateShipPackages(
    $orderId: ID!
    $companyId: ID!
    $expectedRevision: Int!
    $packages: [ShipPackageSizeInput!]!
    $note: String
  ) {
    salesOrderShipPackagesUpdate(
      orderId: $orderId
      companyId: $companyId
      expectedRevision: $expectedRevision
      packages: $packages
      note: $note
    ) {
      changedPackageCount
      bundleOrderIds
      bundleNumber
      billableWeightBefore
      billableWeightAfter
      order {
        id
        ${SHIP_PACKAGE_FIELDS}
      }
    }
  }
`;

/** Where the Box Sizes page lives. */
export const shipPackagesPath = (orderId: string) => `/sales-order-packages/${orderId}`;
